import "@/App.css";
import React, { lazy as reactLazy, Suspense } from "react";
import { Loader2 } from "lucide-react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { I18nProvider } from "@/i18n";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
// After a deploy, a tab opened before it may ask for a chunk that no longer exists.
// Reload once to pick up the new version instead of showing a broken screen.
const lazy = (load) => reactLazy(() => load().then((m) => {
  sessionStorage.removeItem("chunk-reload");
  return m;
}).catch((err) => {
  if (!sessionStorage.getItem("chunk-reload")) {
    sessionStorage.setItem("chunk-reload", "1");
    window.location.reload();
    return new Promise(() => {});
  }
  throw err;
}));

// Each screen is its own chunk, loaded when first opened: the landing page doesn't download the app,
// and charts (Reports) or PDF tooling (patient detail) only load where they're used.
const DashboardLayout = lazy(() => import("@/components/DashboardLayout"));
const Landing = lazy(() => import("@/pages/Landing"));
const Auth = lazy(() => import("@/pages/Auth"));
const InviteAccept = lazy(() => import("@/pages/InviteAccept"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Profile = lazy(() => import("@/pages/Profile"));
const Patients = lazy(() => import("@/pages/Patients"));
const PatientDetail = lazy(() => import("@/pages/PatientDetail"));
const Appointments = lazy(() => import("@/pages/Appointments"));
const Doctors = lazy(() => import("@/pages/Doctors"));
const Clinics = lazy(() => import("@/pages/Clinics"));
const Catalogs = lazy(() => import("@/pages/Catalogs"));
const Reports = lazy(() => import("@/pages/Reports"));
const Users = lazy(() => import("@/pages/Users"));
const Followups = lazy(() => import("@/pages/Followups"));
const Settings = lazy(() => import("@/pages/Settings"));

const FullPageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background" role="status">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

// Role-restricted pages redirect to the dashboard instead of rendering a 403-filled screen.
const Guard = ({ roles, children }) => <ProtectedRoute roles={roles}>{children}</ProtectedRoute>;

function Router() {
  return (
    <Suspense fallback={<FullPageLoader />}>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/invite/:token" element={<InviteAccept />} />
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="profile" element={<Guard roles={["super_admin", "clinic", "doctor", "assistant"]}><Profile /></Guard>} />
        <Route path="patients" element={<Patients />} />
        <Route path="patients/:id" element={<PatientDetail />} />
        <Route path="appointments" element={<Appointments />} />
        <Route path="followups" element={<Guard roles={["clinic", "doctor", "assistant"]}><Followups /></Guard>} />
        <Route path="doctors" element={<Guard roles={["super_admin", "clinic"]}><Doctors /></Guard>} />
        <Route path="clinics" element={<Guard roles={["super_admin"]}><Clinics /></Guard>} />
        <Route path="catalogs" element={<Guard roles={["super_admin"]}><Catalogs /></Guard>} />
        <Route path="reports" element={<Guard roles={["super_admin", "clinic"]}><Reports /></Guard>} />
        <Route path="users" element={<Guard roles={["super_admin", "clinic"]}><Users /></Guard>} />
        <Route path="settings" element={<Guard roles={["clinic"]}><Settings /></Guard>} />
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Route>
    </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <Router />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}
